/**
 * Utilidades para mapear y formatear títulos de preguntas de JotForm
 * Basado en la lógica de useJotFormData.tsx y DashboardLayout.tsx
 */

export interface ProcessedAnswer {
  questionId: string;
  questionText: string;
  mappedName: string;
  answerValue: any;
  answerType: 'rating' | 'demographic' | 'choice' | 'text' | 'matrix' | 'unknown';
  metadata?: {
    isNumeric?: boolean;
    isShortText?: boolean;
    isDemographic?: boolean;
    // Metadata original del campo de JotForm (para control_matrix, etc.)
    originalField?: any;
  };
}

// Mapeo de nombres de preguntas para versiones más cortas
const questionNameMapping: Record<string, string> = {
  'experiencia': 'Experiencia general',
  'atención': 'Atención recibida',
  'tiempo que tarda': 'Tiempo de respuesta',
  'probabilidad': 'Probabilidad recomendación',
  'recomend': 'Probabilidad recomendación',
  'valora del 1 al 10 el servicio de las áreas': 'Servicio de áreas'
};

// Keywords para identificar preguntas demográficas
const demographicKeywords = [
  'edad', 'sexo', 'genero', 'género', 'área', 'area',
  'rol', 'puesto', 'cargo', 'ciudad', 'tiempo'
];

// Preguntas de opción múltiple permitidas
const allowedChoiceQuestions = [
  '¿considera que fue clara y comprensible la información proporcionada?',
  '¿su problema o consulta fue resuelto de manera efectiva?',
  '¿tiene claros los entregables de las solicitudes de asesoría jurídica?',
  '¿conoce el sla (acuerdo de nivel de servicios) para las solicitudes de asesoría jurídica?'
];

// Preguntas abiertas permitidas
const allowedOpenQuestions = [
  '¿cuál es el valor agregado que está aportando esta asesoría jurídica?',
  '¿en qué medida este servicio le proporciona seguridad jurídica?',
  '¿qué es lo que más valora de este servicio jurídico?',
  '¿qué le ha faltado para que este servicio jurídico cumpliera sus necesidades?',
  'comparta alguna experiencia en la que se ha sentido apoyado por este servicio',
  'comparta alguna experiencia en la que el servicio no fue satisfactorio',
  '¿en qué servicio de jurídico ha experimentado más dificultades y debería ser nuestra prioridad de mejora?'
];

// Mapeo de filas de matriz a áreas
export const rowToAreaMap: Record<number, string> = {
  0: 'Área Fiscal',
  1: 'Área Colegios',
  2: 'Área Universidades',
  3: 'Área Centros y Obras apostólicas',
  4: 'Área Contenciosos',
  5: 'Área Archivo',
  6: 'Dirección Jurídico'
};

/**
 * Limpia el texto de la pregunta removiendo paréntesis al final
 */
export function cleanQuestionText(text: string): string {
  return text.replace(/\s*\([^)]*\)\s*$/, '').trim();
}

/**
 * Mapea el título de una pregunta a su versión corta si existe
 */
export function mapQuestionName(questionText: string): string {
  const lowerQuestion = questionText.toLowerCase();

  for (const [keyword, displayName] of Object.entries(questionNameMapping)) {
    if (lowerQuestion.includes(keyword.toLowerCase())) {
      return displayName;
    }
  }

  return questionText;
}

/**
 * Determina el tipo de pregunta basándose en el contenido de la respuesta
 */
export function determineQuestionType(
  questionText: string,
  answerValue: any
): 'rating' | 'demographic' | 'choice' | 'text' | 'matrix' | 'unknown' {
  const lowerQuestion = questionText.toLowerCase();

  // Detectar preguntas de matriz (áreas)
  if (lowerQuestion.includes('valora del 1 al 10 el servicio de las áreas') ||
      lowerQuestion.includes('servicio de las áreas') ||
      lowerQuestion.includes('áreas con la') ||
      lowerQuestion.includes('trato habitual')) {
    return 'matrix';
  }

  // Detectar ratings numéricos (1-10)
  const numericValue = parseFloat(answerValue);
  if (!isNaN(numericValue) && numericValue >= 1 && numericValue <= 10 && Number.isInteger(numericValue)) {
    return 'rating';
  }

  // Detectar preguntas demográficas
  if (demographicKeywords.some(keyword => lowerQuestion.includes(keyword))) {
    return 'demographic';
  }

  // Detectar preguntas de opción múltiple
  if (typeof answerValue === 'string' && answerValue.length < 100) {
    const isAllowedChoice = allowedChoiceQuestions.some(allowed =>
      lowerQuestion.includes(allowed.toLowerCase()) ||
      allowed.toLowerCase().includes(lowerQuestion)
    );

    if (isAllowedChoice) {
      return 'choice';
    }
  }

  // Detectar preguntas abiertas
  if (typeof answerValue === 'string' && answerValue.length >= 100) {
    const isAllowedOpen = allowedOpenQuestions.some(allowed =>
      lowerQuestion.includes(allowed.toLowerCase()) ||
      allowed.toLowerCase().includes(lowerQuestion.replace(/[^\w\sáéíóúñü]/g, ''))
    );

    if (isAllowedOpen) {
      return 'text';
    }
  }

  return 'unknown';
}

/**
 * Procesa una respuesta de matriz (áreas)
 */
export function processMatrixAnswer(answer: any): Array<{ area: string; rating: number }> {
  const results: Array<{ area: string; rating: number }> = [];

  if (!Array.isArray(answer)) {
    return results;
  }

  answer.forEach((row: any[], rowIndex: number) => {
    const areaName = rowToAreaMap[rowIndex];

    if (areaName && Array.isArray(row)) {
      row.forEach((value, colIndex) => {
        if (value && value !== '' && value !== null && value !== undefined) {
          const rating = colIndex + 1;
          if (rating >= 1 && rating <= 10) {
            results.push({ area: areaName, rating });
          }
        }
      });
    }
  });

  return results;
}

/**
 * Formatea el valor de la respuesta para mostrar
 */
export function formatAnswerValue(answer: any): string {
  if (answer === null || answer === undefined) {
    return 'Sin respuesta';
  }

  // Manejar objetos complejos (como nombres {first, last})
  if (typeof answer === 'object' && answer !== null) {
    if (answer.first && answer.last) {
      return `${answer.first} ${answer.last}`;
    }
    if (answer.value) {
      return answer.value;
    }
    if (Array.isArray(answer)) {
      return answer.join(', ');
    }
    return JSON.stringify(answer, null, 2);
  }

  return String(answer);
}

/**
 * Procesa todas las respuestas de un submission
 */
export function processSubmissionAnswers(answers: Record<string, any>): ProcessedAnswer[] {
  if (!answers || typeof answers !== 'object') {
    return [];
  }

  const processedAnswers: ProcessedAnswer[] = [];

  Object.entries(answers).forEach(([questionId, answer]: [string, any]) => {
    let questionText = answer.text || answer.name || `Pregunta ${questionId}`;
    questionText = cleanQuestionText(questionText);

    const answerValue = answer.answer || answer.text || '';
    const mappedName = mapQuestionName(questionText);
    const answerType = determineQuestionType(questionText, answerValue);

    processedAnswers.push({
      questionId,
      questionText,
      mappedName,
      answerValue,
      answerType,
      metadata: {
        isNumeric: !isNaN(parseFloat(answerValue)),
        isShortText: typeof answerValue === 'string' && answerValue.length < 100,
        isDemographic: demographicKeywords.some(kw => questionText.toLowerCase().includes(kw)),
        // Preservar el campo original completo para tipos especiales como control_matrix
        originalField: answer
      }
    });
  });

  return processedAnswers;
}

/**
 * Agrupa respuestas por tipo
 */
export function groupAnswersByType(processedAnswers: ProcessedAnswer[]): {
  ratings: ProcessedAnswer[];
  demographics: ProcessedAnswer[];
  choices: ProcessedAnswer[];
  texts: ProcessedAnswer[];
  matrix: ProcessedAnswer[];
  others: ProcessedAnswer[];
} {
  return {
    ratings: processedAnswers.filter(a => a.answerType === 'rating'),
    demographics: processedAnswers.filter(a => a.answerType === 'demographic'),
    choices: processedAnswers.filter(a => a.answerType === 'choice'),
    texts: processedAnswers.filter(a => a.answerType === 'text'),
    matrix: processedAnswers.filter(a => a.answerType === 'matrix'),
    others: processedAnswers.filter(a => a.answerType === 'unknown')
  };
}
